import type { BadgeOffer } from "./badge/client.ts";
import type { TravelEvent } from "./travel/wire.ts";
import type { HarvestStateEvent } from "./harvest/wire.ts";
import type { SkyOverride, CompanionNamingState, DialogueCloseReason, DoorLine, EdgeViewRecord, ExploredSet, FeedbackAccessState, MapPlaces, PartyPositionsRecord, PartyVitalsRecord } from "odyssey-shared";
import type { CombatRecord, EffectsRecord, HarvestEventRecord, HarvestListRecord, LooksRecord, TargetEffectsRecord, ProgressRecord, ProjectilesRecord, PvpStateRecord, PvpSwitchRecord, SnapshotRecord, ThingEventRecord, VitalsRecord } from "odyssey-shared";
import type { BagEvent } from "./bag/wire.ts";
import type { LootEvent } from "./chest/wire.ts";
import type { WalletAmounts } from "./wallet/wire.ts";
import type { AttributesEvent } from "./character/wire.ts";
import type { ChatEvent } from "./chat/events.ts";
import type { EdgeNearFrame, HandoffFrame } from "./handoff-wire.ts";
import { GameConnection, type ConnectionOptions, type DialogueLine, type Interaction, type RespawnOffer, type Shrines, type Welcome } from "./connection.ts";
import type { ShownEmoji } from "./emotes/wire.ts";
import type { MaintenanceNotice } from "./hud/maintenance-warning.ts";
import type { ModerationEvent } from "./moderation/feed.ts";
import type { LeaveAnswer } from "./leave-desk.ts";
import type { QueueFrame } from "./queue/queue-state.ts";
import type { BarEvent } from "./bar/wire.ts";
import type { ToolBeltEvent } from "./belt/wire.ts";

/**
 * The game page's connections to the gateway (P1.4-T5): one at a time, each given `JOIN_TIMEOUT_MS` to be
 * welcomed and as long again, from the welcome, to send the first snapshot the page applies, which the
 * reveal waits on. A connection given up on (`drop`), or replaced by the next (`open`), has its socket
 * closed, and its later frames and close never reach the scene, so a retry after a failure is never
 * answered by the socket that failed. What a close or a silence means is the scene's to decide.
 *
 * A `hello` that must wait for room is answered with `queue` frames, at least one every 5 seconds, until its
 * `welcome` (ADR-127 decision 5): each restarts the wait for the welcome, so a page in the login queue is given up
 * only when the frames stop for a whole `JOIN_TIMEOUT_MS`.
 */

/**
 * How long a connection may wait for the server's welcome, and then for the first snapshot the page applies
 * after it, before it is given up as a server that did not answer. A socket to a host that never answers
 * waits on the browser's own connection timeout, which can run for minutes, and a server that welcomes the
 * page and sends no state would leave the loading shell on its last step with nothing to retry; the gateway
 * answers `hello` at once, a live map server sends a snapshot every tick, and the account requests give up after
 * the same ten seconds (`auth.ts`). A snapshot the page discards does not end the wait, since the scene has
 * nothing to draw from it.
 */
export const JOIN_TIMEOUT_MS = 10_000;

/**
 * How long the page waits, at most, for the gateway to answer the close of a connection it releases (P1.9-T62,
 * ADR-065's note "Change character and the release of the character"): the gateway's close handler takes the
 * character out of the world as it answers, so the answer is the release; a gateway that never answers costs the
 * player no more than this, and the next join replaces the stale connection.
 */
export const RELEASE_BOUND_MS = 2_000;

/**
 * What a connection waits on under its deadline: the server's welcome, then the first snapshot the page
 * applies after it.
 */
export type JoinAwait = "welcome" | "snapshot";

/**
 * What the scene does with the current connection's frames and its close, and with a server that sent
 * nothing it waited on in time, whose socket the join has already given up. `onChat` hears its chat frames
 * (ADR-068), `onToolBelt` its `toolBelt` (ADR-142 decision 3b), `onBag` its inventory frames, `onWallet` its `wallet` (ADR-124 decision 4.1), `onBar` its `bar` (ADR-121 decision 5.1) and `onLoot` its chest frames (ADR-091 decision 20), `onVitals` the player's own `vitals` (ADR-074 decision 5), `onPvpState` its `pvpState` (ADR-073's
 * amendment of 2026-09-27), `onPvpSwitch` its `pvpSwitch` (ADR-076), `onProgress` its `progress` (ADR-083),
 * `onEffects` its `effects` (ADR-079),
 * `onTargetEffects` its `targetEffects` (ADR-079's amendment for P2.2-T4), `onCombat`
 * its `combat` frames (decision 3), `onMaintenance` the maintenance schedule (ADR-078), `onFeedbackOffer` the offer of both
 * kinds of report and `onFeedbackAccess` the account's access to feedback (ADR-081's amendment of 2026-10-05), `onModeration` a moderation message
 * (ADR-056's amendment of 2026-10-04), `onRespawnOffer` the respawn
 * offered to the dead player (ADR-073's amendment "the respawn is the player's"), `onShrines` the shrines the player's
 * character has discovered and `onThingEvent` the events on things in view (ADR-096), `onLooks` the looks of the
 * characters entering its view (ADR-094 decision 6), and `onProjectiles` the shots launched in its view (ADR-095
 * decision 11). `onQueue` hears its place in the login queue while its `hello` waits for room (ADR-127 decision 5), and
 * nothing of it once the welcome has arrived.
 */
export interface JoinHandlers {
  readonly onWelcome: (welcome: Welcome) => void;
  readonly onSnapshot: (record: SnapshotRecord) => void;
  readonly onInteraction: (interaction: Interaction) => void;
  /** Hears a `dialogue` (ADR-136 decision 5.3). */
  readonly onDialogue?: (line: DialogueLine) => void;
  /** Hears the server's `dialogueClose` (ADR-136 decision 5.5). */
  readonly onDialogueClose?: (handle: number, reason: DialogueCloseReason) => void;
  /** Hears the server's `companionNaming` (ADR-142 decision 5.4). */
  readonly onCompanionNaming?: (state: CompanionNamingState) => void;
  /** Hears the server's `sky` (PROTOCOL.md 6.100.0). */
  readonly onSky?: (sky: SkyOverride) => void;
  /**
   * Hears a `source` (PROTOCOL.md 6.103.0): an energy source went out (`lit` false) or was relit.
   */
  readonly onSource?: (key: string, lit: boolean) => void;
  readonly onChat?: (event: ChatEvent) => void;
  /** Hears each keepalive round trip in milliseconds (P1.9-T46). */
  readonly onRoundTrip?: (rttMs: number) => void;
  readonly onBag?: (event: BagEvent) => void;
  readonly onWallet?: (amounts: WalletAmounts) => void;
  readonly onAttributes?: (event: AttributesEvent) => void;
  /** Hears the map server's `travelOffer` and `travelState` (ADR-137 decision 12). */
  readonly onTravel?: (event: TravelEvent) => void;
  /** Hears the map server's `harvestState`, and each chunk's `harvested` list and `harvestEvent` frame (ADR-142 decision 3). */
  readonly onHarvestState?: (event: HarvestStateEvent) => void;
  readonly onHarvested?: (list: Readonly<HarvestListRecord>) => void;
  readonly onHarvestEvent?: (events: Readonly<HarvestEventRecord>) => void;
  readonly onBar?: (event: BarEvent) => void;
  readonly onToolBelt?: (event: ToolBeltEvent) => void;
  /** Hears the map server's `companionState` (ADR-142 decision 6): whether the player's companion is out, and his name. */
  readonly onCompanionState?: (out: boolean, name: string) => void;
  readonly onLoot?: (event: LootEvent) => void;
  /** Hears the gateway's `emotes`, the emote sets the account holds (ADR-114 decision 6.2). */
  readonly onEmotes?: (sets: readonly string[]) => void;
  /** Hears the map server's `emojiShown` and `actionShown`, a picture over a character in view (ADR-114 decision 6.5). */
  readonly onEmojiShown?: (emoji: ShownEmoji) => void;
  readonly onVitals?: (vitals: Readonly<VitalsRecord>) => void;
  readonly onPvpState?: (pvp: Readonly<PvpStateRecord>) => void;
  readonly onPvpSwitch?: (pvpSwitch: Readonly<PvpSwitchRecord>) => void;
  readonly onProgress?: (progress: Readonly<ProgressRecord>) => void;
  readonly onEffects?: (effects: Readonly<EffectsRecord>) => void;
  readonly onTargetEffects?: (effects: Readonly<TargetEffectsRecord>) => void;
  readonly onCombat?: (combat: Readonly<CombatRecord>) => void;
  readonly onMaintenance?: (notice: MaintenanceNotice) => void;
  readonly onQueue?: (queue: QueueFrame) => void;
  readonly onLeaveAnswer?: (answer: LeaveAnswer) => void;
  readonly onRespawnOffer?: (offer: RespawnOffer) => void;
  /** Hears `handoffInitiated`; the join's deadline is lifted, since the handoff keeps its own (`HANDOFF_STALL_MS`, ADR-106 decision 19). */
  readonly onHandoff?: (handoff: HandoffFrame) => void;
  /** Hears `doorRefused` (ADR-122 decision 2.8). */
  readonly onDoorRefused?: (reason: DoorLine) => void;
  /** Hears `doorPrompt` (PROTOCOL.md 6.93.0). */
  readonly onDoorPrompt?: (shown: boolean, name: string) => void;
  readonly onShrines?: (shrines: Shrines) => void;
  /** Hears each `mapPlaces` (ADR-100 decision 6) and the join's `explored` (decision 22). */
  readonly onMapPlaces?: (places: MapPlaces) => void;
  readonly onExplored?: (set: ExploredSet) => void;
  readonly onPartyVitals?: (vitals: Readonly<PartyVitalsRecord>) => void;
  readonly onPartyPositions?: (positions: Readonly<PartyPositionsRecord>) => void;
  /** Hears each `edgeView` (ADR-147 decision 13). */
  readonly onEdgeView?: (view: Readonly<EdgeViewRecord>) => void;
  /** Hears each `edgeNear` (ADR-147 decisions 13 and 26). */
  readonly onEdgeNear?: (near: EdgeNearFrame) => void;
  readonly onThingEvent?: (events: Readonly<ThingEventRecord>) => void;
  readonly onLooks?: (looks: Readonly<LooksRecord>) => void;
  readonly onProjectiles?: (projectiles: Readonly<ProjectilesRecord>) => void;
  readonly onFeedbackOffer?: () => void;
  readonly onFeedbackAccess?: (state: FeedbackAccessState, endsInMs: number) => void;
  readonly onModeration?: (event: ModerationEvent) => void;
  readonly onStaffBadgeOffer?: (offer: BadgeOffer) => void;
  readonly onDiscarded: () => void;
  readonly onClose: (code: number, reason: string) => void;
  readonly onSilent: (awaited: JoinAwait) => void;
}

/**
 * Where to connect and how: the gateway's URL, the token to present (read at each connection, so each presents
 * the newest the page holds), the content version, the artificial latency, and the deadline for the welcome
 * and again for the first snapshot. The socket and the timer are the page's own unless a test hands in
 * others.
 */
export interface JoinOptions {
  readonly url: string;
  readonly token: () => string;
  readonly contentVersion: number;
  readonly latencyMs: number;
  readonly deadlineMs: number;
  readonly connect?: (options: ConnectionOptions) => GameConnection;
  /**
   * Whether a Game Master has frozen the player now, which each connection reads at every send
   * (`ConnectionOptions.frozen`).
   */
  readonly frozen?: () => boolean;
  readonly schedule?: (delayMs: number, run: () => void) => () => void;
}

/**
 * Runs a callback after a delay on the page's timer.
 *
 * @param delayMs The delay in milliseconds.
 * @param run The callback.
 * @returns A function that cancels it.
 */
function afterDelay(delayMs: number, run: () => void): () => void {
  const timer = setTimeout(run, delayMs);
  return () => clearTimeout(timer);
}

/**
 * The page's connections to the gateway.
 */
export class Join {
  private attempt = 0;
  private current: GameConnection | null = null;
  /**
   * The smoothed round trip the page's last connection had measured as it was given up, null before any.
   */
  private roundTripMs: number | null = null;
  private cancelDeadline: (() => void) | null = null;

  /**
   * @param options Where to connect and how.
   * @param handlers What the scene does with what the current connection hears.
   */
  public constructor(
    private readonly options: JoinOptions,
    private readonly handlers: JoinHandlers,
  ) {}

  /**
   * The current connection.
   *
   * @returns The connection, or null before the first and once one is given up.
   */
  public get connection(): GameConnection | null {
    return this.current;
  }

  /**
   * Opens a connection presenting the page's newest token, giving up any before it, and gives the server
   * the deadline to welcome it, then the deadline again from the welcome to send the first snapshot the page
   * applies; with either not sent in time and no close, the connection is given up and `onSilent` names what
   * was missing. A `queue` frame before the welcome starts the welcome's deadline again.
   */
  public open(): void {
    this.drop();
    const attempt = this.attempt;
    const handlers = this.handlers;
    const schedule = this.options.schedule ?? afterDelay;
    /**
     * Whether this connection is still the current one.
     *
     * @returns True until it is given up or replaced.
     */
    const live = (): boolean => attempt === this.attempt;
    let awaited: JoinAwait | null = null;
    let waits = 0;
    /**
     * Waits on the next thing the connection needs under a fresh deadline, or on nothing. A deadline replaced by a
     * later wait gives nothing up, should its timer still fire.
     *
     * @param next The welcome or the first snapshot, or null once the snapshot arrived or the socket closed.
     */
    const wait = (next: JoinAwait | null): void => {
      this.clearDeadline();
      awaited = next;
      waits += 1;
      const mine = waits;
      if (next !== null) {
        this.cancelDeadline = schedule(this.options.deadlineMs, () => {
          if (live() && waits === mine) {
            this.drop();
            handlers.onSilent(next);
          }
        });
      }
    };
    wait("welcome");
    const connect = this.options.connect ?? ((options: ConnectionOptions): GameConnection => new GameConnection(options));
    this.current = connect({
      url: this.options.url,
      token: this.options.token(),
      contentVersion: this.options.contentVersion,
      latencyMs: this.options.latencyMs,
      roundTripMs: this.roundTripMs,
      ...(this.options.frozen === undefined ? {} : { frozen: this.options.frozen }),
      onWelcome: (welcome) => {
        if (live()) {
          wait("snapshot");
          handlers.onWelcome(welcome);
        }
      },
      onSnapshot: (record) => {
        if (live()) {
          if (awaited === "snapshot") {
            wait(null);
          }
          handlers.onSnapshot(record);
        }
      },
      onInteraction: (interaction) => {
        if (live()) {
          handlers.onInteraction(interaction);
        }
      },
      onDialogue: (line) => {
        if (live()) {
          handlers.onDialogue?.(line);
        }
      },
      onDialogueClose: (handle, reason) => {
        if (live()) {
          handlers.onDialogueClose?.(handle, reason);
        }
      },
      onCompanionNaming: (state) => {
        if (live()) {
          handlers.onCompanionNaming?.(state);
        }
      },
      onSky: (sky) => {
        if (live()) {
          handlers.onSky?.(sky);
        }
      },
      onSource: (key, lit) => {
        if (live()) {
          handlers.onSource?.(key, lit);
        }
      },
      onChat: (event) => {
        if (live()) {
          handlers.onChat?.(event);
        }
      },
      onRoundTrip: (rttMs) => {
        if (live()) {
          handlers.onRoundTrip?.(rttMs);
        }
      },
      onBag: (event) => {
        if (live()) {
          handlers.onBag?.(event);
        }
      },
      onWallet: (amounts) => {
        if (live()) {
          handlers.onWallet?.(amounts);
        }
      },
      onAttributes: (event) => {
        if (live()) {
          handlers.onAttributes?.(event);
        }
      },
      onTravel: (event) => {
        if (live()) {
          handlers.onTravel?.(event);
        }
      },
      onHarvestState: (event) => {
        if (live()) {
          handlers.onHarvestState?.(event);
        }
      },
      onHarvested: (list) => {
        if (live()) {
          handlers.onHarvested?.(list);
        }
      },
      onHarvestEvent: (events) => {
        if (live()) {
          handlers.onHarvestEvent?.(events);
        }
      },
      onBar: (event) => {
        if (live()) {
          handlers.onBar?.(event);
        }
      },
      onToolBelt: (event) => {
        if (live()) {
          handlers.onToolBelt?.(event);
        }
      },
      onCompanionState: (out, name) => {
        if (live()) {
          handlers.onCompanionState?.(out, name);
        }
      },
      onLoot: (event) => {
        if (live()) {
          handlers.onLoot?.(event);
        }
      },
      onEmotes: (sets) => {
        if (live()) {
          handlers.onEmotes?.(sets);
        }
      },
      onEmojiShown: (emoji) => {
        if (live()) {
          handlers.onEmojiShown?.(emoji);
        }
      },
      onVitals: (vitals) => {
        if (live()) {
          handlers.onVitals?.(vitals);
        }
      },
      onPvpState: (pvp) => {
        if (live()) {
          handlers.onPvpState?.(pvp);
        }
      },
      onPvpSwitch: (pvpSwitch) => {
        if (live()) {
          handlers.onPvpSwitch?.(pvpSwitch);
        }
      },
      onProgress: (progress) => {
        if (live()) {
          handlers.onProgress?.(progress);
        }
      },
      onEffects: (effects) => {
        if (live()) {
          handlers.onEffects?.(effects);
        }
      },
      onTargetEffects: (effects) => {
        if (live()) {
          handlers.onTargetEffects?.(effects);
        }
      },
      onCombat: (combat) => {
        if (live()) {
          handlers.onCombat?.(combat);
        }
      },
      onMaintenance: (notice) => {
        if (live()) {
          handlers.onMaintenance?.(notice);
        }
      },
      onQueue: (queue) => {
        if (live() && awaited === "welcome") {
          wait("welcome");
          handlers.onQueue?.(queue);
        }
      },
      onLeaveAnswer: (answer) => {
        if (live()) {
          handlers.onLeaveAnswer?.(answer);
        }
      },
      onHandoff: (handoff) => {
        if (live()) {
          wait(null);
          handlers.onHandoff?.(handoff);
        }
      },
      onDoorRefused: (reason) => {
        if (live()) {
          handlers.onDoorRefused?.(reason);
        }
      },
      onDoorPrompt: (shown, name) => {
        if (live()) {
          handlers.onDoorPrompt?.(shown, name);
        }
      },
      onRespawnOffer: (offer) => {
        if (live()) {
          handlers.onRespawnOffer?.(offer);
        }
      },
      onShrines: (shrines) => {
        if (live()) {
          handlers.onShrines?.(shrines);
        }
      },
      onMapPlaces: (places) => {
        if (live()) {
          handlers.onMapPlaces?.(places);
        }
      },
      onExplored: (set) => {
        if (live()) {
          handlers.onExplored?.(set);
        }
      },
      onPartyVitals: (vitals) => {
        if (live()) {
          handlers.onPartyVitals?.(vitals);
        }
      },
      onPartyPositions: (positions) => {
        if (live()) {
          handlers.onPartyPositions?.(positions);
        }
      },
      onEdgeView: (view) => {
        if (live()) {
          handlers.onEdgeView?.(view);
        }
      },
      onEdgeNear: (near) => {
        if (live()) {
          handlers.onEdgeNear?.(near);
        }
      },
      onThingEvent: (events) => {
        if (live()) {
          handlers.onThingEvent?.(events);
        }
      },
      onLooks: (looks) => {
        if (live()) {
          handlers.onLooks?.(looks);
        }
      },
      onProjectiles: (projectiles) => {
        if (live()) {
          handlers.onProjectiles?.(projectiles);
        }
      },
      onFeedbackOffer: () => {
        if (live()) {
          handlers.onFeedbackOffer?.();
        }
      },
      onFeedbackAccess: (state, endsInMs) => {
        if (live()) {
          handlers.onFeedbackAccess?.(state, endsInMs);
        }
      },
      onModeration: (event) => {
        if (live()) {
          handlers.onModeration?.(event);
        }
      },
      onStaffBadgeOffer: (offer) => {
        if (live()) {
          handlers.onStaffBadgeOffer?.(offer);
        }
      },
      onDiscarded: () => {
        if (live()) {
          handlers.onDiscarded();
        }
      },
      onClose: (code, reason) => {
        if (live()) {
          wait(null);
          handlers.onClose(code, reason);
        }
      },
    });
  }

  /**
   * Gives the current connection up, as `drop` does, and waits for its socket to close, which for a normal close is
   * once the gateway has answered it, and so has taken the character out of the world.
   *
   * @param boundMs The longest wait, in milliseconds.
   * @returns A promise that settles once the socket has closed or the bound has passed, and at once with no
   *   connection; it never rejects.
   */
  public release(boundMs: number): Promise<void> {
    const current = this.current;
    this.drop();
    if (current === null) {
      return Promise.resolve();
    }
    const schedule = this.options.schedule ?? afterDelay;
    return new Promise<void>((resolve) => {
      const cancel = schedule(boundMs, resolve);
      void current.ended.then(() => {
        cancel();
        resolve();
      });
    });
  }

  /**
   * Gives the current connection up: its socket is closed and its later frames and close are ignored. The round trip
   * it had measured is kept for the connection opened next (ADR-095 note 52).
   */
  public drop(): void {
    this.attempt += 1;
    this.clearDeadline();
    this.roundTripMs = this.current?.perf.smoothedRttMs ?? this.roundTripMs;
    this.current?.close();
    this.current = null;
  }

  /**
   * Stops waiting under the deadline.
   */
  private clearDeadline(): void {
    this.cancelDeadline?.();
    this.cancelDeadline = null;
  }
}
